import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sanitizeBroadcastHtmlClient } from '../../lib/sanitizeHtmlClient';
import type { AudienceRecipient } from '../../types/broadcast';
import { previewContent, type PreviewContentRequest } from './api';
import type { ComposeDraft } from './BroadcastCompose';

// Stage 6A §5.2 (right pane). Picks a sample recipient and shows the server-
// resolved content (slugs filled, email rendered to sanitised HTML) plus the
// audience-wide unresolved-link warnings. The body HTML is sanitised server-
// side; we re-sanitise here before injecting into the DOM as defence-in-depth.

type BroadcastPreviewPaneProps = {
  draft: ComposeDraft;
  recipients: AudienceRecipient[];
  audienceLoading: boolean;
};

export function BroadcastPreviewPane({
  draft,
  recipients,
  audienceLoading,
}: BroadcastPreviewPaneProps) {
  const [selectedId, setSelectedId] = useState<string>('');

  // Keep the selection valid as the audience changes.
  useEffect(() => {
    if (recipients.length === 0) {
      if (selectedId) setSelectedId('');
      return;
    }
    if (!recipients.some((r) => r.contactId === selectedId)) {
      setSelectedId(recipients[0]!.contactId);
    }
  }, [recipients, selectedId]);

  // Debounce the request payload so typing doesn't spam the endpoint.
  const request = useMemo<PreviewContentRequest | null>(() => {
    if (!selectedId) return null;
    return {
      audienceScope: draft.audienceScope,
      channels: draft.channels,
      emailSubject: draft.emailSubject,
      emailBodyMarkdown: draft.emailBodyMarkdown,
      smsBody: draft.smsBody,
      emailFromMode: draft.emailFromMode,
      emailFromCustomName: draft.emailFromCustomName,
      emailReplyToMode: draft.emailReplyToMode,
      emailReplyToCustomEmail: draft.emailReplyToCustomEmail,
      contactId: selectedId,
    };
  }, [draft, selectedId]);

  const [debounced, setDebounced] = useState<PreviewContentRequest | null>(request);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(request), 400);
    return () => window.clearTimeout(t);
  }, [request]);

  const preview = useQuery({
    queryKey: ['broadcast-preview-content', debounced],
    queryFn: () => previewContent(debounced!),
    enabled: debounced !== null,
  });

  const wantsEmail = draft.channels.includes('email');
  const wantsSms = draft.channels.includes('sms');

  if (audienceLoading) {
    return <p className="text-sm text-slate-500">Loading recipients…</p>;
  }
  if (recipients.length === 0) {
    return <p className="text-sm text-slate-400">Select an audience to preview content.</p>;
  }

  const data = preview.data;
  const safeBodyHtml = data?.bodyHtml ? sanitizeBroadcastHtmlClient(data.bodyHtml) : '';

  return (
    <div className="space-y-4">
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Preview as</span>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
        >
          {recipients.map((r) => (
            <option key={r.contactId} value={r.contactId}>
              {r.contactName}
              {r.email ? ` · ${r.email}` : r.phone ? ` · ${r.phone}` : ''}
            </option>
          ))}
        </select>
      </label>

      {preview.isLoading && <p className="text-sm text-slate-500">Resolving…</p>}
      {preview.error && <p className="text-sm text-red-600">Couldn't resolve preview.</p>}

      {data && wantsEmail && (
        <div className="rounded-md border border-slate-200">
          <div className="border-b border-slate-100 px-3 py-2 text-xs text-slate-500">
            <div>
              From: <span className="text-slate-800">{data.fromName || '—'}</span>
            </div>
            {data.replyToEmail && (
              <div>
                Reply-To: <span className="text-slate-800">{data.replyToEmail}</span>
              </div>
            )}
            <div className="mt-1 font-medium text-slate-900">{data.subject || '(no subject)'}</div>
          </div>
          <div
            className="prose prose-sm max-w-none px-3 py-2 text-sm"
            // eslint-disable-next-line react/no-danger -- sanitised server-side + re-sanitised here.
            dangerouslySetInnerHTML={{ __html: safeBodyHtml }}
          />
        </div>
      )}

      {data && wantsSms && (
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">SMS</p>
          <div className="max-w-xs whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-2 text-sm text-slate-900">
            {data.smsBody || '(empty)'}
          </div>
        </div>
      )}

      {data && data.warnings.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="mb-1 text-sm font-medium text-amber-800">Unresolved links</p>
          <ul className="space-y-0.5 text-xs text-amber-700">
            {data.warnings.map((w) => (
              <li key={w.token}>
                {w.recipientsAffected} recipient{w.recipientsAffected === 1 ? '' : 's'} missing{' '}
                <span className="font-medium">{w.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
